import { useMemo } from 'react';

import { useSearchContext } from './useSearchContext';
import {
  filterByFreeText as defaultFilterByFreeText,
  makeCriterionFilter,
} from '../client-side';

export const useFilterWithExcludedCriteria = <T = any>(
  criteria: string[] | string,
  enabled: boolean | undefined
): T[] => {
  if (!Array.isArray(criteria)) {
    criteria = [criteria];
  }
  const ctx = useSearchContext();
  if (!ctx) {
    throw new Error(
      'useFilterWithExcludedCriteria must be used inside search context'
    );
  }
  const {
    clientCriteria,
    values,
    data,
    freeText,
    disableFreeText,
    freeTextServerSearch,
    freeTextPredicate,
    filterByFreeText = defaultFilterByFreeText,
  } = ctx;

  const clientSideFreeTextSearchEnabled =
    !disableFreeText && !freeTextServerSearch;
  const clientSideFreeText = clientSideFreeTextSearchEnabled
    ? freeText
    : undefined;

  const criteriaKey = JSON.stringify(criteria);

  return useMemo(() => {
    const filters: Array<(opt: any) => boolean> = [];
    if (!enabled || !clientCriteria?.length || !data) {
      return [];
    }
    for (const criterion of clientCriteria) {
      if (!criteria.includes(criterion.key) && criterion.key in values) {
        filters.push(makeCriterionFilter(criterion, values[criterion.key]));
      }
    }
    const filteredByCriteria = filters?.length
      ? data.filter((option) => filters.every((filter) => filter(option)))
      : data;
    if (!clientSideFreeText) {
      return filteredByCriteria || [];
    }
    return filteredByCriteria
      ? filterByFreeText(
          filteredByCriteria,
          clientSideFreeText,
          freeTextPredicate
        )
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, clientSideFreeText, clientCriteria, values, enabled, criteriaKey]);
};
